import { DeleteOutlined, EditOutlined, ExportOutlined } from '@ant-design/icons'
import { getOperationName } from '@apollo/client/utilities'
import { Card, List, Popconfirm, Tooltip, Typography } from 'antd'
import Avatar from 'antd/lib/avatar/avatar'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { Link } from 'react-router-dom'

import { UserHoverCard } from '@/components'
import {
  GetKnowledgeGroupPostsDocument,
  RequestedKnowledgePostFragment,
  useGetViewerQuery,
  useKnowledgePostDeleteMutation,
} from '@/generated/graphql'
import { useIsSystemAdmin } from '@/modules/admin/admin.hooks'
import { KnowledgeCreateEditModal } from '@/modules/knowledge/knowledge-modal/knowledge-create-edit-modal.component'
import { PostCardFiles } from '@/modules/post/post-card/post-card-files.component'
import { routes } from '@/routes'

import s from './knowledge-card.module.less'

export const KnowledgeCard: React.FC<{ knowledgePost: RequestedKnowledgePostFragment; type?: 'listItem' | 'card' }> = ({
  knowledgePost,
  type = 'listItem',
}) => {
  const history = useHistory()
  const { t } = useTranslation()

  const { data } = useGetViewerQuery()

  const [knowledgePostDelete] = useKnowledgePostDeleteMutation({
    refetchQueries: [getOperationName(GetKnowledgeGroupPostsDocument)!],
  })

  const isSystemAdmin = useIsSystemAdmin()

  const redirectUrl = (url?: string) => {
    if (!url) {
      return
    }
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  if (!data) {
    return null
  }

  const canOperate = data.viewer.userId === knowledgePost.creatorUserId || isSystemAdmin

  const title = (
    <div className={s.title}>
      <Typography.Title level={5}>
        {knowledgePost.files && knowledgePost.files?.length > 0 ? (
          <Link to={routes.knowledgebase.knowledge(knowledgePost.knowledgePostId)}>{knowledgePost.title}</Link>
        ) : (
          knowledgePost.title
        )}
      </Typography.Title>
    </div>
  )

  const edit = canOperate && (
    <KnowledgeCreateEditModal knowledgePost={knowledgePost} knowledgeGroupId={knowledgePost.knowledgeGroupId}>
      <EditOutlined style={{ fontSize: 16, color: '#48bbb5' }} />
    </KnowledgeCreateEditModal>
  )

  const remove = canOperate && (
    <Popconfirm
      title={t('knowledge.confirmDelete')}
      onConfirm={() => knowledgePostDelete({ variables: { knowledgePostId: knowledgePost.knowledgePostId } })}
    >
      <DeleteOutlined style={{ fontSize: 16, color: '#cd3737' }} />
    </Popconfirm>
  )

  const link = knowledgePost.link && (
    <Tooltip title={t('knowledge.link')}>
      <ExportOutlined onClick={() => redirectUrl(knowledgePost.link?.url)} style={{ fontSize: 16 }} />
    </Tooltip>
  )

  const actions = [link, edit, remove].filter(Boolean)

  const item =
    type === 'listItem' ? (
      <List.Item actions={actions}>
        <List.Item.Meta
          title={title}
          description={knowledgePost.description}
          avatar={
            <UserHoverCard email={knowledgePost.author.email}>
              <Avatar
                style={{ cursor: 'pointer' }}
                onClick={() => history.push(routes.users.page(knowledgePost.author.userId))}
                size={24}
                src={knowledgePost.author.avatarUrl}
              />
            </UserHoverCard>
          }
        />
      </List.Item>
    ) : (
      <Card actions={actions} title={knowledgePost.title}>
        <Card.Meta description={knowledgePost.description || knowledgePost.link?.url} />
        {knowledgePost.files && (
          <div style={{ marginTop: 16 }}>
            <PostCardFiles files={knowledgePost.files} />
          </div>
        )}
      </Card>
    )

  return <div className={s.item}>{item}</div>
}
